import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import type { LogicGraph, SimulationNode } from './LogicGraph';

/** A native Set at the API boundary, with resolved handles for internal waves. */
export class SimulationWorkSet extends Set<NodeHandle> {
  #handles: SimulationNode[] = [];
  #owner?: LogicGraph;
  #revision = -1;

  #currentGraph: () => LogicGraph;
  constructor(currentGraph: () => LogicGraph) {
    super();
    this.#currentGraph = currentGraph;
  }

  override add(id: NodeHandle): this {
    const before = this.size;
    super.add(id);
    if (this.size !== before)
      this.#handles.push(this.#currentGraph().getSimulationNode(id));
    return this;
  }

  addNode(node: SimulationNode): void {
    const before = this.size;
    super.add(node.id);
    if (this.size !== before) this.#handles.push(node);
  }

  override delete(id: NodeHandle): boolean {
    if (!super.delete(id)) return false;
    this.#handles = this.#handles.filter((node) => node.id !== id);
    return true;
  }

  override clear(): void {
    super.clear();
    this.#handles = [];
    this.#owner = undefined;
    this.#revision = -1;
  }

  takeNodes(graph: LogicGraph): SimulationNode[] {
    if (
      this.#owner !== graph ||
      this.#revision !== graph.topologyRevision ||
      !graph.isTopologyTracked ||
      this.#handles.length !== this.size
    ) {
      this.#handles = Array.from(this, (id) => graph.getSimulationNode(id));
      this.#owner = graph;
      this.#revision = graph.topologyRevision;
    }
    const pending = this.#handles;
    super.clear();
    this.#handles = [];
    return pending;
  }
}
